import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  NgZone,
  signal
} from '@angular/core';
import {Subject, Subscription, debounceTime} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ServiceApi} from '../../../../network/service.api';

const toRial = (value: number) => Math.round(value).toLocaleString('fa-IR');

/**
 * «اگه طلا خریده بودم...» — ارزش امروزِ مبلغی که چند ماه پیش طلا شده بود.
 * طرح فقط کلاس‌های CSS این بخش را داشت؛ نتیجه از همان سرویس benefit-history
 * می‌آید که ماشین‌حساب قبلی صفحه با آن کار می‌کرد.
 */
@Component({
  selector: 'app-benefit-calc',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './benefit-calc.component.html',
  styleUrl: './benefit-calc.component.scss'
})
export class BenefitCalcComponent {

  private readonly api = inject(ServiceApi);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  readonly periods = [
    {months: 1, label: '۱ ماه'},
    {months: 3, label: '۳ ماه'},
    {months: 6, label: '۶ ماه'},
    {months: 12, label: '۱ سال'}
  ];

  readonly presets = [
    {amount: 10_000_000, label: '۱۰ میلیون'},
    {amount: 500_000_000, label: '۵۰۰ میلیون'},
    {amount: 2_000_000_000, label: '۲ میلیارد'}
  ];

  readonly amount = signal(500_000_000);
  readonly months = signal(12);
  /** ارزش امروز به ریال، همان‌طور که سرویس برگردانده */
  readonly result = signal<number | null>(null);
  /** عددی که روی صفحه است؛ با انیمیشن به result می‌رسد */
  readonly shown = signal<number | null>(null);
  readonly loading = signal(false);
  readonly failed = signal(false);

  readonly amountText = computed(() => this.amount() ? toRial(this.amount()) : '');
  readonly periodLabel = computed(() => this.periods.find(p => p.months === this.months())?.label ?? '');

  readonly outcome = computed(() => {
    const value = this.result();
    const amount = this.amount();
    if (value === null || !amount) return null;
    const profit = value - amount;
    const pct = profit / amount * 100;
    const pctText = Math.abs(pct).toLocaleString('fa-IR', {maximumFractionDigits: 1});
    return {
      gain: profit >= 0,
      text: `${profit >= 0 ? '▲' : '▼'} ${toRial(Math.abs(profit))} ریال ${profit >= 0 ? 'سود' : 'زیان'} · ${pctText}٪`,
      // نوار همان درصد رشد را نشان می‌دهد؛ بیش از ۱۰۰٪ یعنی نوار پر
      bar: Math.max(0, Math.min(100, pct))
    };
  });

  readonly shownText = computed(() => {
    const value = this.shown();
    return value === null ? '—' : toRial(value);
  });

  private readonly requests = new Subject<void>();
  private request?: Subscription;
  private readonly cache = new Map<string, number>();
  private frame = 0;
  private started = false;

  constructor() {
    this.requests.pipe(debounceTime(400), takeUntilDestroyed()).subscribe(() => this.fetch());

    // تا بخش به دید نزدیک نشده، درخواستی نمی‌رود
    afterNextRender(() => {
      if (typeof IntersectionObserver === 'undefined') {
        this.start();
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(e => e.isIntersecting)) return;
        observer.disconnect();
        this.zone.run(() => this.start());
      }, {rootMargin: '400px 0px'});
      observer.observe(this.host.nativeElement);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });

    // روی سرور requestAnimationFrame وجود ندارد و frame صفر می‌ماند
    this.destroyRef.onDestroy(() => {
      if (this.frame) cancelAnimationFrame(this.frame);
    });
  }

  private start() {
    this.started = true;
    this.fetch();
  }

  setMonths(months: number) {
    if (months === this.months()) return;
    this.months.set(months);
    this.fetch();
  }

  setAmount(amount: number) {
    if (amount === this.amount()) return;
    this.amount.set(amount);
    this.fetch();
  }

  onAmountInput(event: Event) {
    const input = event.target as HTMLInputElement;
    // ارقام فارسی و عربی هم پذیرفته می‌شوند؛ هر چیز دیگری حذف می‌شود
    const digits = input.value
      .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
      .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
      .replace(/\D/g, '')
      .slice(0, 15);
    const amount = Number(digits) || 0;
    this.amount.set(amount);
    // بایندینگ [value] وقتی عدد عوض نشده (مثلاً حرف تایپ شده) مقدار را برنمی‌گرداند
    input.value = this.amountText();
    if (this.started) this.requests.next();
  }

  private fetch() {
    if (!this.started) return;
    this.request?.unsubscribe();

    const amount = this.amount();
    if (!amount) {
      this.result.set(null);
      this.animateTo(null);
      this.loading.set(false);
      return;
    }

    const time = this.purchaseTime(this.months());
    const key = `${amount}|${time}`;
    const cached = this.cache.get(key);
    if (cached !== undefined) {
      this.show(cached);
      return;
    }

    this.loading.set(true);
    this.failed.set(false);
    this.request = this.api.getBenefitHistory(amount, time).subscribe({
      next: res => {
        this.loading.set(false);
        const value = Number(res?.data);
        if (!res || res.data == null || !isFinite(value)) {
          this.failed.set(true);
          return;
        }
        this.cache.set(key, value);
        this.show(value);
      },
      error: () => {
        this.loading.set(false);
        this.failed.set(true);
      }
    });
  }

  /** ابتدای همان روزِ چند ماه پیش؛ مثل ماشین‌حساب قبلی صفحه */
  private purchaseTime(months: number) {
    const date = new Date();
    date.setMonth(date.getMonth() - months);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  }

  private show(value: number) {
    this.loading.set(false);
    this.failed.set(false);
    this.result.set(value);
    this.animateTo(value);
  }

  /** عدد نمایشی را نرم به مقدار تازه می‌رساند (مثل AnimatedNumber طرح) */
  private animateTo(target: number | null) {
    cancelAnimationFrame(this.frame);
    const from = this.shown();
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (target === null || from === null || reduce) {
      this.shown.set(target);
      return;
    }

    const start = performance.now();
    const duration = 900;
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);
    // بیرون از zone تا هر فریم کل برنامه را بررسی نکند؛ سیگنال خودش صفحه را به‌روز می‌کند
    this.zone.runOutsideAngular(() => {
      const step = (now: number) => {
        const p = Math.min((now - start) / duration, 1);
        this.shown.set(p < 1 ? from + (target - from) * ease(p) : target);
        if (p < 1) this.frame = requestAnimationFrame(step);
      };
      this.frame = requestAnimationFrame(step);
    });
  }
}
